/**
 * Utility functions for Insights page data processing and formatting
 */

/**
 * Convert any value to a number, defaulting to 0 if invalid
 */
export const toNum = (v: any): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

/**
 * Safely divide two numbers, returning null if division is invalid
 */
export const safeDiv = (a: any, b: any): number | null => {
  const numA = Number(a);
  const numB = Number(b);
  if (!isFinite(numA) || !isFinite(numB) || numB === 0) return null;
  return numA / numB;
};

/**
 * Format a number as currency (USD)
 * @param value - The number to format
 * @param showZero - Whether to show $0.00 for null/undefined values
 */
export const formatCurrency = (value: number | null, showZero: boolean = false): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return showZero ? '$0.00' : '';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a number with specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 */
export const formatNumber = (value: number | null, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @param label - Label for user feedback (optional)
 */
export const copyToClipboard = async (text: string, label?: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    const message = label ? `Failed to copy ${label}` : 'Failed to copy to clipboard';
    alert(message);
    throw err;
  }
};
